import { createStore, Effect, Event } from 'effector';

import { applyReducers } from '@m/effector-utils';

import {
  getUser,
  logout,
  parseEnterpriseInviteToken,
  parseInvitationCode,
  parseMemberReferralInvitationCode,
  registerCustomerAndAcceptTerms,
  requestCode,
  resetUserAsyncStatuses,
  setTokenAndUserInfoForEnterpriseInvitation,
  setUser,
  signUpRegister,
  updateUser,
  verifyAndRegister,
  verifyCode,
} from './actions';

import type { VerifyAndRegisterResult } from './actions';
import type { DoneHandler } from '@m/effector-utils';
import type { User } from '@m/types/api';
import type { EnterpriseInvitationUser } from '@m/types/api/EnterpriseInvitation';
import type { APIResponse } from '@m/utils/http';

export type UserState = {
  authenticated: boolean | null;
  enterpriseUser?: EnterpriseInvitationUser;
  invitation:
    | {
        invitationCode: string | undefined;
        phoneNumber: string | undefined;
      }
    | { [key: string]: undefined };
  isInvitedUser?: boolean;
  isReferredMember?: boolean;
  phoneNumber: string | null;
  signUpErrors: { key: string; reason: string }[] | null | undefined;
  // TODO: fix type to be 'User | null'
  user: User | { [key: string]: undefined };
};

const initialState: UserState = {
  user: {},
  invitation: {},
  phoneNumber: null,
  signUpErrors: null,
  authenticated: null,
};

const store = createStore(initialState);

const updateUserState = (
  state: UserState,
  { result: { success, data } }: { result: APIResponse },
) => ({
  ...state,
  user: success ? data.user : {},
  authenticated: success && data.user.isRegistered,
});

type Reducers = {
  getUser: {
    action: Effect<Parameters<typeof getUser>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getUser>[0], UserState>;
  };
  logout: {
    action: Effect<Parameters<typeof logout>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof logout>[0], UserState>;
    skipAsyncStore: boolean;
  };
  parseEnterpriseInviteToken: {
    action: Effect<
      { token: string },
      { enterpriseUser?: EnterpriseInvitationUser; user: User | null }
    >;
    done: (
      state: UserState,
      payload: { result: { enterpriseUser?: EnterpriseInvitationUser; user: User | null } },
    ) => UserState;
  };
  parseInvitationCode: {
    action: Effect<Parameters<typeof parseInvitationCode>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof parseInvitationCode>[0], UserState>;
  };
  parseMemberReferralInvitationCode: {
    action: Effect<Parameters<typeof parseMemberReferralInvitationCode>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof parseMemberReferralInvitationCode>[0], UserState>;
  };
  registerCustomerAndAcceptTerms: {
    action: Effect<Parameters<typeof registerCustomerAndAcceptTerms>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof registerCustomerAndAcceptTerms>[0], UserState>;
  };
  requestCode: {
    action: Effect<Parameters<typeof requestCode>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof requestCode>[0], UserState>;
  };
  setTokenAndUserInfoForEnterpriseInvitation: {
    action: Event<{ enterpriseUser: EnterpriseInvitationUser }>;
    reducer: (state: UserState, payload: { enterpriseUser: EnterpriseInvitationUser }) => UserState;
  };
  setUser: {
    action: Event<{ user: User | null }>;
    reducer: (state: UserState, payload: { user: User | null }) => UserState;
  };
  signUpRegister: {
    action: Effect<Parameters<typeof signUpRegister>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof signUpRegister>[0], UserState>;
  };
  updateUser: {
    action: Effect<Parameters<typeof updateUser>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof updateUser>[0], UserState>;
  };
  verifyAndRegister: {
    action: Effect<Parameters<typeof verifyAndRegister>[0], VerifyAndRegisterResult>;
    done: (state: UserState, payload: { result: VerifyAndRegisterResult }) => UserState;
  };
  verifyCode: {
    action: Effect<Parameters<typeof verifyCode>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof verifyCode>[0], UserState>;
  };
};

export const reducers: Reducers = {
  getUser: {
    action: getUser,
    done: updateUserState,
  },
  parseInvitationCode: {
    action: parseInvitationCode,
    done: (state, { params: { invitationCode }, result: { success, data } }) => {
      if (success) {
        return {
          ...state,
          invitation: {
            invitationCode,
            isRegistered: data.user.isRegistered,
            phoneNumber: data.user.phoneNumber,
            licensePlateText: data.licensePlate ? data.licensePlate.text : null,
          },
          isReferredMember: false,
          isInvitedUser: true,
          phoneNumber: data.user.phoneNumber,
          user: data.user,
          authenticated: data.user.isRegistered,
        };
      }
      return state;
    },
  },
  parseMemberReferralInvitationCode: {
    action: parseMemberReferralInvitationCode,
    done: (state, { params: { invitationCode }, result: { success, data } }) => {
      if (success) {
        return {
          ...state,
          invitation: {
            invitationCode,
            isRegistered: data.user.isRegistered,
            phoneNumber: data.user.phoneNumber,
            licensePlateText: data.licensePlate ? data.licensePlate.text : null,
            licensePlateState: data.licensePlate ? data.licensePlate.state : null,
          },
          isInvitedUser: false,
          isReferredMember: true,
          phoneNumber: data.user.phoneNumber,
          user: data.user,
          authenticated: data.user.isRegistered,
        };
      }
      return state;
    },
  },
  signUpRegister: {
    action: signUpRegister,
    done: (state, { result: { success, data, errors } }) => ({
      ...state,
      user: success ? data.user : {},
      authenticated: success && data.user.isRegistered,
      signUpErrors: success ? null : errors,
    }),
  },
  registerCustomerAndAcceptTerms: {
    action: registerCustomerAndAcceptTerms,
    done: (state, { result: { success, data, errors } }) => ({
      ...state,
      user: success ? data.user : state.user,
      authenticated: success ? data.user.isRegistered : state.authenticated,
    }),
  },
  requestCode: {
    action: requestCode,
    done: (state, { params: { phoneNumber } }) => ({
      ...state,
      phoneNumber,
    }),
  },
  verifyAndRegister: {
    action: verifyAndRegister,
    done: (state, { result }) => ({
      ...state,
      user: result.user,
      authenticated: result.user.isRegistered,
    }),
  },
  verifyCode: {
    action: verifyCode,
    done: (state, { result: { success, data } }) => ({
      ...state,
      token: success ? data.token : null,
      user: success ? data.user : {},
      authenticated: success && data.user.isRegistered,
    }),
  },
  logout: {
    action: logout,
    done: () => ({
      // Not using store.reset so we can set authenticated to false
      ...initialState,
      authenticated: false,
    }),
    skipAsyncStore: true,
  },
  setUser: {
    action: setUser,
    reducer: (state, { user }) => ({
      ...state,
      user: user || {},
      authenticated: !!user && user.isRegistered,
    }),
  },
  setTokenAndUserInfoForEnterpriseInvitation: {
    action: setTokenAndUserInfoForEnterpriseInvitation,
    reducer: (state, { enterpriseUser }) => ({
      ...state,
      enterpriseUser,
    }),
  },
  updateUser: {
    action: updateUser,
    done: (state, { result: { success, data, errors } }) => ({
      ...state,
      user: {
        ...state.user,
        ...(success ? data : {}),
      },
    }),
  },
  parseEnterpriseInviteToken: {
    action: parseEnterpriseInviteToken,
    done: (state, { result: { user, enterpriseUser } }) => ({
      ...state,
      user: user || {},
      authenticated: !!user && user.isRegistered,
      enterpriseUser,
    }),
  },
};

// Not resetting store so we can set authenticated to false
const asyncResetReducers = [logout.done, resetUserAsyncStatuses];

export default applyReducers({ storeName: 'customer/user', store, reducers, asyncResetReducers });
